import React, {memo, useCallback, useMemo, useState} from "react"
import {PasswordContainer} from "../../../../components/PasswordContainer"
import {SettingsPageContainer} from "../../../../components/Settings/SettingsPageContainer"
import {useHistory, useLocation, useParams} from "react-router-dom"
import {useQuestion} from "../../../../hooks/question"
import {useMessage} from "../../../../hooks/message"
import {useRPC} from "../../../../hooks/rpc"
import {useKeyboard} from "../../../../hooks/keyboard"
import {SettingInputComponent} from "../../../../components/Settings/SettingInputComponent"
import {useSelector} from "react-redux"
import {useLocalization} from "../../../../hooks/localization"
import {CallListModel} from "../../../../types/control"

const BACK_URL = "/settings/intercom/call-list"

export const CallListEditScreen = memo(function CallListEditScreen() {
    const {t} = useLocalization()
    const params = useParams<{id: string}>()
    const question = useQuestion()
    const {success, failure} = useMessage()
    const [rpc] = useRPC()
    const location = useLocation<CallListModel>()
    const history = useHistory()

    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard()
    const [setActiveNumpadInput, renderNumpadKeyboard, currentNumpadInput] = useKeyboard("numpad", true)
    const [setActiveNumpadExtendedInput, renderNumpadExtendedKeyboard, currentNumpadExtendedInput] = useKeyboard(
        "numpad-extended",
        true
    )

    const allSearchList = useSelector(state => state.control.callListList)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const selected = useMemo(() => location.state || allSearchList.find(list => list.id === params?.id) || null, [])

    const [buttonName, setButtonName] = useState((selected?.id != null && selected?.button) || "")
    const [deviceToSearch, setDeviceToSearch] = useState(selected?.sought_ip || "")

    const onBackPressed = useCallback(() => history.push(BACK_URL), [history])

    const onDeletePressed = useCallback(() => {
        question(t("are_you_sure_you_want_to_delete_call_log"), () => {
            return rpc
                .deleteCallList(selected?.id)
                .then(() => {
                    success(t("call_log_deleted_successfully"))
                    onBackPressed()
                })
                .catch(failure)
        })
    }, [failure, t, onBackPressed, question, rpc, selected?.id, success])

    const isFieldsValid = useCallback((): boolean => {
        if (buttonName == null || buttonName.length === 0 || isNaN(Number(buttonName))) {
            failure(t("pls_enter_a_valid_button_number"))

            return false
        }
        if (deviceToSearch == null || deviceToSearch.length === 0) {
            failure(t("pls_enter_a_valid_call_device_name"))

            return false
        }

        return true
    }, [t, failure, buttonName, deviceToSearch])

    const onCreatePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return
        }

        const model: CallListModel = {
            id: selected?.id || null,
            button: buttonName,
            sought_ip: deviceToSearch
        }

        return rpc
            .updateCallList(model)
            .then(() => {
                const message = selected?.id ? t("call_log_edited") : t("call_log_created")
                success(message)
                onBackPressed()
            })
            .catch(failure)
    }, [t, isFieldsValid, success, failure, onBackPressed, rpc, selected?.id, deviceToSearch, buttonName])

    const buttons = useMemo(
        () => [
            {
                text: t("delete"),
                className: "mulberry-wood",
                onClick: onDeletePressed,
                hidden: !selected?.id
            },
            {
                text: t("save"),
                className: "green",
                onClick: onCreatePressed
            },
            {
                text: t("cancel"),
                className: "red",
                url: BACK_URL
            }
        ],
        [t, onCreatePressed, onDeletePressed, selected]
    )

    const onBlur = useCallback(() => {
        setActiveInput(null)
        setActiveNumpadInput(null)
        setActiveNumpadExtendedInput(null)
    }, [setActiveInput, setActiveNumpadExtendedInput, setActiveNumpadInput])

    return (
        <PasswordContainer requiredRole="service" isFullscreen={true}>
            <SettingsPageContainer
                buttons={buttons}
                className="service-edit"
                backUrl={BACK_URL}
                currentInput={currentInput}
                currentNumpadInput={currentNumpadInput}
                currentNumpadExtendedInput={currentNumpadExtendedInput}
            >
                <div className="content-divider">
                    <div className="content-wrapper">
                        <SettingInputComponent
                            placeholder={t("enter_a_button_name")}
                            label={t("button")}
                            value={buttonName}
                            setValue={setButtonName}
                            setActiveInput={setActiveNumpadInput}
                            onBlur={onBlur}
                        />
                        <SettingInputComponent
                            placeholder={t("write_device_to_search")}
                            label={t("device_to_search")}
                            value={deviceToSearch}
                            setValue={setDeviceToSearch}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                        />
                    </div>
                    <div className="content-wrapper"></div>
                </div>
            </SettingsPageContainer>
            {renderKeyboard()}
            {renderNumpadKeyboard()}
            {renderNumpadExtendedKeyboard()}
        </PasswordContainer>
    )
})
